import { useState } from "react"
import { useAuth } from "@/components/auth-context"
import { supabase } from "@/lib/supabase"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2, Send, CheckCircle2 } from "lucide-react"
import { useTranslation } from "react-i18next"

interface FeedbackDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
    const { t } = useTranslation()
    const { user } = useAuth()
    const [type, setType] = useState<string>("feature")
    const [message, setMessage] = useState("")
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user || !message.trim()) return

        setLoading(true)
        setError(null)

        try {
            const { error: submitError } = await supabase
                .from('feedback')
                .insert({
                    user_id: user.id,
                    type,
                    message: message.trim()
                })

            if (submitError) throw submitError

            setSuccess(true)
            setMessage("")
            setTimeout(() => {
                setSuccess(false)
                onOpenChange(false)
            }, 2000)
        } catch (e: unknown) {
            console.error(e)
            setError(t('help.feedbackDialog.errorMessage'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('help.feedbackDialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('help.feedbackDialog.description')}
                    </DialogDescription>
                </DialogHeader>

                {success ? (
                    <div className="py-8 flex flex-col items-center justify-center text-center space-y-3 animate-in fade-in zoom-in duration-300">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <CheckCircle2 className="w-6 h-6" />
                        </div>
                        <h3 className="font-semibold text-lg">{t('help.feedbackDialog.successTitle')}</h3>
                        <p className="text-sm text-muted-foreground">{t('help.feedbackDialog.successMessage')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="type">{t('help.feedbackDialog.typeLabel')}</Label>
                            <Select value={type} onValueChange={setType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="feature">{t('help.feedbackDialog.typeFeature')}</SelectItem>
                                    <SelectItem value="bug">{t('help.feedbackDialog.typeBug')}</SelectItem>
                                    <SelectItem value="other">{t('help.feedbackDialog.typeOther')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="message">{t('help.feedbackDialog.messageLabel')}</Label>
                            <Textarea
                                id="message"
                                placeholder={type === 'bug' ? t('help.feedbackDialog.placeholderBug') : t('help.feedbackDialog.placeholderFeature')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="min-h-[120px] resize-none"
                                required
                            />
                        </div>

                        {error && (
                            <p className="text-sm text-red-500 font-medium">{error}</p>
                        )}

                        <DialogFooter className="pt-2">
                            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={loading || !message.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90">
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('help.feedbackDialog.sending')}
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-4 w-4" />
                                        {t('help.feedbackDialog.submit')}
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    )
}
